import React from 'react'
import classNames from 'classnames'
import { PricingCardProps } from './types.d'
import { Card, Text } from '@toasttab/shared/src/utils/craftUtils'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Icon } from '@toasttab/shared/src/utils/craftUtils'
import StyledList from '@toasttab/shared/src/components/molecules/StyledList/StyledList'

const PricingCard = ({
  accentColor,
  accentPosition,
  badgeColor,
  badgeText,
  children, // styledList
  listHeader,
  ctaLabel,
  ctaLink,
  padding,
  packagePrice,
  packagePriceMonthly = '',
  pricePrefix,
  priceSuffix,
  sectionDescription,
  sectionHeading,
  bottomLink,
  textAlignment,
  fourCardLayout = false,
  dataTestId = 'PricingCard-1'
}: PricingCardProps) => {
  const containerClasses = classNames(
    'bg-white relative rounded-md border border-card-light pricing-card h-full',
    {
      flex: bottomLink
    }
  )
  const pricingCardClasses = classNames({
    'box-border gap-y-4': true,
    '!p-6': !bottomLink,
    'inline-grid !px-6 !pb-4 !pt-6': bottomLink,
    'lg:grid-rows-[minmax(auto,310px)_auto]': fourCardLayout && !bottomLink
  })
  const topSectionClasses = classNames({
    'inline-grid gap-y-4 grid-rows[minmax(auto,272px)_auto] md:grid-rows-[minmax(auto,252px)_auto]':
      packagePrice && packagePrice.trim() !== ''
  })

  const accentClasses = classNames({
    'accent-top absolute h-4 rounded-tl-[8px] rounded-tr-[8px] -top-[1px] -left-[1px] -right-[1px] before:content-[""] before:absolute before:top-0 before:block before:w-full before:h-1 overflow-hidden':
      accentPosition === 'top',
    'accent-bottom absolute h-4 rounded-bl-[8px] rounded-br-[8px] -bottom-[1px] -left-[1px] -right-[1px] before:content-[""] before:absolute before:bottom-0 before:block before:w-full before:h-1 overflow-hidden':
      accentPosition === 'bottom',

    'before:bg-blue-75': accentColor === 'blue-75',
    'before:bg-yellow-50': accentColor === 'yellow-50',
    'before:bg-brand-50': accentColor === 'brand-50'
  })
  const innerClasses = classNames(
    Card.cardPadding(padding),
    Text.alignment(textAlignment),
    pricingCardClasses
  )

  const convertSymbolsToWords = (text: string): string => {
    if (!text) return text

    // Replace symbols with words
    let converted = text.replace(/\//g, ' per ').replace(/\+/g, ' plus ')

    // Remove any double spaces
    converted = converted.replace(/\s+/g, ' ').trim()

    return converted
  }

  const listItems: any[] = []
  if (Array.isArray(children)) {
    children.forEach((child: any) => {
      if (Array.isArray(child.listItems) && child.listItems.length > 0) {
        listItems.push({
          ...child.listItems[0],
          listStyles: child.listStyles
        })
      }
    })
  }

  return (
    <div className={containerClasses} data-testid={dataTestId}>
      {accentPosition === 'top' && (
        <div className={accentClasses} data-testid='PricingCard-accent--top' />
      )}
      <div className={innerClasses} data-testid='PricingCard-inner'>
        <div className='inline-grid content-between'>
          <div className={topSectionClasses}>
            <div className='inline-grid'>
              {badgeText && badgeColor && (
                <div className='absolute -top-[0.875rem] left-0 right-0 text-center'>
                  <Badge
                    badgeColor={badgeColor}
                    text={badgeText}
                    uppercase
                    classes='!font-normal'
                  />
                </div>
              )}
              <div>
                {sectionHeading && (
                  <div className='mb-6 border-b border-b-gray-50'>
                    <Typography
                      variation='h4'
                      as='h2'
                      classes='mb-4 text-[22px] sm:text-[26px] !font-normal'
                    >
                      {sectionHeading}
                    </Typography>
                  </div>
                )}
                {sectionDescription && (
                  <Typography
                    variation='default'
                    as='p'
                    classes='text-secondary'
                  >
                    {sectionDescription}
                  </Typography>
                )}
              </div>
              <div className='self-center'>
                {pricePrefix && (
                  <Typography
                    as='p'
                    variation='default'
                    classes='text-secondary !text-xs'
                  >
                    {pricePrefix}
                  </Typography>
                )}
                {packagePrice && (
                  <p>
                    <Typography
                      as='span'
                      classes='!text-[31px] sm:!text-[2.813rem] !leading-[40px] sm:!leading-[45px] !text-default !font-effra'
                      variation='default'
                    >
                      {packagePrice}
                    </Typography>
                    {packagePriceMonthly && (
                      <Typography
                        as='span'
                        ariaLabel={convertSymbolsToWords(packagePriceMonthly)}
                        variation='default'
                        classes='text-secondary'
                      >
                        {packagePriceMonthly}
                      </Typography>
                    )}
                  </p>
                )}
                {priceSuffix && (
                  <Typography
                    ariaLabel={convertSymbolsToWords(priceSuffix)}
                    as='p'
                    variation='default'
                    classes='text-secondary'
                  >
                    {priceSuffix}
                  </Typography>
                )}
              </div>
            </div>
            <div className='mt-2'>
              {ctaLabel && ctaLink && (
                <Button
                  href={ctaLink}
                  variation='primary'
                  fontWeight={['font-semibold']}
                  size='medium'
                  classes='mb-8'
                  fullwidth={
                    packagePrice && packagePrice.trim() !== '' ? false : true
                  }
                >
                  {ctaLabel}
                </Button>
              )}
              {listHeader && (
                <>
                  <div>
                    <Typography
                      as='p'
                      variation='default'
                      classes='text-secondary'
                    >
                      {listHeader}
                    </Typography>
                  </div>
                  <div className='mt-3 mb-6 border-b border-b-gray-30' />
                </>
              )}
              {listItems.length > 0 && (
                <StyledList
                  listOnly
                  sectionHeading=''
                  sectionDescription=''
                  listVariation='heading-only'
                  listStyles='none'
                  listItems={listItems}
                />
              )}
            </div>
          </div>
        </div>
        {bottomLink && (
          <div className='mt-5 pt-2 border-t border-t-gray-30 self-end'>
            <TextLink
              description={`${sectionHeading ? sectionHeading : 'Link for '} ${
                bottomLink.label
              }`}
              href={bottomLink.url}
              text={bottomLink.label}
              icon={Icon.linkIcons(bottomLink.icon)}
              classes='mt-2'
            />
          </div>
        )}
      </div>
      {accentPosition === 'bottom' && (
        <div
          className={accentClasses}
          data-testid='PricingCard-accent--bottom'
        />
      )}
    </div>
  )
}

export default PricingCard
